/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { PropsWithChildren, useEffect } from 'react'
import Image from 'next/legacy/image'
import { Link } from 'lib/i18n/routing'
import { urlStartsWithHashiCorp } from 'lib/determine-hashicorp-url'
import { IconArrowRight24 } from '@hashicorp/flight-icons/svg-react/arrow-right-24'
import { IconArrowRight16 } from '@hashicorp/flight-icons/svg-react/arrow-right-16'
import { useInView } from 'react-intersection-observer'
import { trackGoal as trackFathomGoal } from 'fathom-client'
import s from './style.module.css'

// DUPLICATE COMPONENT: from industries

export interface ResourcesProps {
	heading: string
	card: {
		type: string
		slug: string
		url: string
		author?: {
			name: string
			image: {
				src: string
			}
		}
		image: {
			src: string
			width: number
			height: number
			alt: string
			blurDataUrl: string
		}
		title: string
		description: string
	}
	items: Array<{
		type: string
		title: string
		slug: string
		url: string
	}>
}

interface ResourceLinkProps extends PropsWithChildren {
	className?: string
	url: string
}

const ResourceLink = ({ className, url, children }: ResourceLinkProps) => {
	if (
		url.startsWith('/') ||
		url.startsWith('#') ||
		urlStartsWithHashiCorp(url)
	) {
		return (
			<Link href={url} className={className}>
				{children}
			</Link>
		)
	}
	return (
		<a
			className={className}
			target="_blank"
			rel="noopener noreferrer"
			href={url}
		>
			{children}
		</a>
	)
}

export const Resources = ({ heading, card, items }: ResourcesProps) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		initialInView: false,
	})

	useEffect(() => {
		if (inView) {
			// Tracking scroll distance, if Resources is viewed,
			// trigger Fathom event.
			trackFathomGoal('DPVFWEME', 0)
		}
	}, [inView])

	return (
		<div className={s.root} ref={ref}>
			<div className={s.container}>
				<h2 className={s.heading}>{heading}</h2>
				{card && (
					<article className={s.card}>
						<ResourceLink url={card.url}>
							<Image
								src={card.image.src}
								alt={card.image.alt}
								width={card.image.width}
								height={card.image.height}
								placeholder="blur"
								blurDataURL={card.image.blurDataUrl}
								layout="responsive"
							/>
							<div className={s.cardContent}>
								<h3 className={s.cardHeading}>{card.title}</h3>
								{card.description && (
									<p className={s.cardDescription}>{card.description}</p>
								)}
								<div className={s.cardCta}>
									View <IconArrowRight16 className={s.cardCtaArrow} />
								</div>
							</div>
						</ResourceLink>
					</article>
				)}
				<ul className={s.links}>
					{items.map((item, index) => {
						return (
							// Index is stable
							// eslint-disable-next-line react/no-array-index-key
							<li key={index}>
								<ResourceLink url={item.url} className={s.link}>
									<div className={s.linkInner}>
										<span className={s.linkTitle}>{item.title}</span>
										<IconArrowRight24 className={s.linkArrow} />
									</div>
								</ResourceLink>
							</li>
						)
					})}
				</ul>
			</div>
		</div>
	)
}
